import React, { useCallback, useMemo } from 'react'
import { Field } from '@workwhile/ui'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { ModalAsyncSelect } from 'components/ModalSelect'
import { useCompaniesQuery } from 'queries/companies'
import { useCompanyGroupsQuery } from 'queries/companyGroups'

type OwnerOption = { label: string; value: number }

const fuzzyOptions: FullOptions<OwnerOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

const OWNER_CONFIG = {
  company: {
    label: 'Company',
    placeholder: 'Select a company',
  },
  companyGroup: {
    label: 'Company Group',
    placeholder: 'Select a company group',
  },
} as const

type OwnerPickerProps = {
  ownerType: keyof typeof OWNER_CONFIG
  value?: number | null
  onChange: (value: number | undefined) => void
  error?: string
}

export const OwnerPicker = ({
  ownerType,
  value,
  onChange,
  error,
}: OwnerPickerProps) => {
  const { data: companies } = useCompaniesQuery()
  const { data: companyGroups } = useCompanyGroupsQuery()

  const items = ownerType === 'company' ? companies : companyGroups
  const { label, placeholder } = OWNER_CONFIG[ownerType]

  const options = useMemo(
    () =>
      items
        ?.slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => ({
          label: `${item.name} (${item.id})`,
          value: item.id,
        })) ?? [],
    [items]
  )

  const searcher = useMemo(
    () =>
      new Searcher<OwnerOption, FullOptions<OwnerOption>>(
        options,
        fuzzyOptions
      ),
    [options]
  )

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<OwnerOption>[]

      return Promise.resolve(results)
    },
    [searcher]
  )

  return (
    <Field label={label} error={error}>
      <ModalAsyncSelect
        block
        isClearable
        isSearchable
        placeholder={placeholder}
        defaultOptions={options}
        loadOptions={loadOptions}
        value={options.find((option) => option.value === value) || null}
        onChange={(option) => {
          onChange(option?.value ?? undefined)
        }}
      />
    </Field>
  )
}
